import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import type { CurrencyInput } from '@/client/types/currency';
import { currencyKeys } from './currency.query';

const BASE = '/api/admin/currencies';
const TIMEOUT = { timeout: 30_000 };

export const useCreateCurrencyMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (body: CurrencyInput) => request<any>(BASE, 'POST', body, TIMEOUT),
    'Failed to create currency',
  );
  return useMutation({
    mutationFn: (body: CurrencyInput) => fn(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};

export const useBulkAddCurrenciesMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (body: CurrencyInput[]) => request<any>(`${BASE}/bulk`, 'POST', body, TIMEOUT),
    'Failed to bulk add currencies',
  );
  return useMutation({
    mutationFn: (body: CurrencyInput[]) => fn(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};

export const useUpdateCurrencyMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { id: number } & CurrencyInput) => {
    const { id, ...body } = args;
    return request<any>(`${BASE}/${id}`, 'PUT', body, TIMEOUT);
  }, 'Failed to update currency');
  return useMutation({
    mutationFn: (args: { id: number } & CurrencyInput) => fn(args),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: currencyKeys.all });
      qc.invalidateQueries({ queryKey: currencyKeys.detail(vars.id) });
    },
  });
};

/** Soft-delete: the DELETE route deactivates the currency rather than removing it. */
export const useDeactivateCurrencyMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { id: number }) => request<any>(`${BASE}/${args.id}`, 'DELETE', {}, TIMEOUT),
    'Failed to deactivate currency',
  );
  return useMutation({
    mutationFn: (args: { id: number }) => fn(args),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: currencyKeys.all });
      qc.invalidateQueries({ queryKey: currencyKeys.detail(vars.id) });
    },
  });
};
